import { useEffect, useState } from 'react';
import { api, money } from '../api.js';
import { ReportHead, Empty } from '../components/Shell.jsx';

/**
 * 100000 (or "100000", the BIGINT the server returns) -> "1000.00", for
 * seeding the edit form's amount fields from an existing bound. Display
 * only — nothing in this file turns a typed amount BACK into cents. The
 * route does that (via api/src/lib/money.js's toCents(), the same function
 * every other money field in this codebase goes through), because it can
 * refuse a malformed amount with a clean 400 instead of the browser
 * silently rounding it with float arithmetic.
 */
function centsToRand(c) {
  return c === null || c === undefined ? '' : (Number(c) / 100).toFixed(2);
}

const emptyForm = { supplierNorm: '', minAmount: '', maxAmount: '', keyword: '', accountId: '', taxCodeId: '' };

/**
 * The allocation rules a practice has written, plus what each has actually
 * done: `hits` and `lastHitAt`. A rule that has never fired is either wrong
 * or written for a supplier the client stopped using — shown plainly rather
 * than sitting there looking authoritative.
 *
 * DELETE deactivates rather than removing (the API enforces this — the
 * schema grants no DELETE privilege on allocation_rule): a rule that fired
 * historically still explains those old allocations.
 */
export function AllocationRules({ entity }) {
  const [rules, setRules] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [taxCodes, setTaxCodes] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editing, setEditing] = useState(null); // { id, accountId, taxCodeId, minAmount, maxAmount, keyword }
  const [showInactive, setShowInactive] = useState(false);
  const [err, setErr] = useState(null);
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/api/entities/${entity.id}/allocation-rules`)
    .then((d) => setRules(d.rules)).catch((e) => setErr(e.message));

  useEffect(() => {
    if (!entity?.id) return;
    load();
    Promise.all([
      api.get(`/api/entities/${entity.id}/accounts`),
      api.get(`/api/entities/${entity.id}/tax-codes`),
    ]).then(([a, t]) => {
      setAccounts(a.accounts.filter((x) => x.is_postable));
      setTaxCodes(t.taxCodes.filter((x) => x.direction !== 'output'));
    }).catch(() => {});
  }, [entity?.id]); // eslint-disable-line

  const accountLabel = (id) => {
    const a = accounts.find((x) => x.id === id);
    return a ? `${a.code} — ${a.name}` : '—';
  };

  /**
   * An account the VAT Act denies the input tax on — entertainment, motor
   * cars, club subscriptions. `input_denied` and `default_tax_code` come from
   * the accounts endpoint, which derives them from the one table that holds
   * them (api/src/seed/taxCodes.js); this screen keeps no list of its own.
   *
   * A rule sending Zappas slips to 6400 Entertainment with a claimable VAT
   * code claims input tax s.17(2)(a) denies, on every Zappas slip, silently.
   * The API refuses it; this says so before the button is pressed, and offers
   * the code that is meant.
   */
  const deniedAccount = (id) => {
    const a = accounts.find((x) => x.id === id);
    return a?.input_denied ? a : null;
  };
  const taxCodeIsDenied = (id) => Boolean(taxCodes.find((x) => x.id === id)?.is_input_denied);
  const deniedMismatch = (accountId, taxCodeId) => {
    const a = deniedAccount(accountId);
    return a && !taxCodeIsDenied(taxCodeId) ? a : null;
  };
  const taxLabel = (id) => {
    if (!id) return '—';
    const t = taxCodes.find((x) => x.id === id);
    return t ? `${t.code} — ${t.name}` : '—';
  };

  const create = async () => {
    setBusy(true); setErr(null); setMsg(null);
    try {
      await api.post(`/api/entities/${entity.id}/allocation-rules`, {
        supplierNorm: form.supplierNorm.trim(),
        accountId: form.accountId,
        taxCodeId: form.taxCodeId || undefined,
        // Sent exactly as typed — the route converts to cents via toCents()
        // and refuses anything malformed with a 400. Nothing here does
        // money arithmetic.
        minAmount: form.minAmount.trim() || undefined,
        maxAmount: form.maxAmount.trim() || undefined,
        keyword: form.keyword.trim() || undefined,
      });
      setMsg('Rule created.');
      setForm(emptyForm);
      load();
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };

  const saveEdit = async () => {
    if (!editing) return;
    setBusy(true); setErr(null); setMsg(null);
    try {
      await api.patch(`/api/entities/${entity.id}/allocation-rules/${editing.id}`, {
        accountId: editing.accountId,
        taxCodeId: editing.taxCodeId || null,
        // Blank means "clear the bound" here (PATCH is explicit null-vs-
        // absent), unlike create above where blank means "don't set one".
        minAmount: editing.minAmount.trim() || null,
        maxAmount: editing.maxAmount.trim() || null,
        keyword: editing.keyword.trim() || null,
      });
      setMsg('Rule updated.');
      setEditing(null);
      load();
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };

  const setActive = async (rule, active) => {
    setErr(null); setMsg(null);
    try {
      if (active) await api.patch(`/api/entities/${entity.id}/allocation-rules/${rule.id}`, { active: true });
      else await api.del(`/api/entities/${entity.id}/allocation-rules/${rule.id}`);
      load();
    } catch (e) { setErr(e.message); }
  };

  if (!rules) return <div className="empty">Loading…</div>;

  // The first word is the chain — a rule typed as a single word ("Spar")
  // matches every branch; a full name ("Spar Tygervalley") matches only that
  // one. Shown so a bookkeeper sees which they are about to write.
  const previewChain = form.supplierNorm.trim().toUpperCase().split(/\s+/)[0] || null;
  const previewIsChainWide = form.supplierNorm.trim() && !form.supplierNorm.trim().includes(' ');

  const visible = rules.filter((r) => showInactive || r.active);
  const formDenied = deniedMismatch(form.accountId, form.taxCodeId);
  const editDenied = editing ? deniedMismatch(editing.accountId, editing.taxCodeId) : null;

  return (
    <>
      <ReportHead entity={entity} title="Allocation rules"
                  sub="Spar to Staff welfare, Novicus to Cost of sales — written once, applied to every slip." />

      {err && <div className="error">{err}</div>}
      {msg && <div className="ok">{msg}</div>}

      <h2 className="eyebrow" style={{ margin: '0 0 10px' }}>New rule</h2>
      <div className="form-grid">
        <label className="field"><span>Supplier</span>
          <input value={form.supplierNorm} placeholder="SPAR, or SPAR TYGERVALLEY for one branch"
                 onChange={(e) => setForm({ ...form, supplierNorm: e.target.value })} /></label>
        <label className="field"><span>Account</span>
          <select value={form.accountId} onChange={(e) => setForm({ ...form, accountId: e.target.value })}>
            <option value="">Choose an account</option>
            {accounts.map((a) => <option key={a.id} value={a.id}>{a.code} — {a.name}</option>)}
          </select></label>
        <label className="field"><span>VAT</span>
          <select value={form.taxCodeId} onChange={(e) => setForm({ ...form, taxCodeId: e.target.value })}>
            <option value="">No VAT</option>
            {taxCodes.map((t) => <option key={t.id} value={t.id}>{t.code} — {t.name}</option>)}
          </select></label>
        <label className="field"><span>Min amount</span>
          <input className="input--figure" inputMode="decimal" value={form.minAmount} placeholder="No minimum"
                 onChange={(e) => setForm({ ...form, minAmount: e.target.value })} /></label>
        <label className="field"><span>Max amount</span>
          <input className="input--figure" inputMode="decimal" value={form.maxAmount} placeholder="No maximum"
                 onChange={(e) => setForm({ ...form, maxAmount: e.target.value })} /></label>
        <label className="field"><span>Keyword</span>
          <input value={form.keyword} placeholder="Optional — must appear on the slip"
                 onChange={(e) => setForm({ ...form, keyword: e.target.value })} /></label>
      </div>

      {previewChain && (
        <div className="note">
          {previewIsChainWide
            ? <>This will apply to slips from any supplier starting <strong>{previewChain}</strong>.</>
            : <>This matches only suppliers whose name normalises to exactly <strong>{form.supplierNorm.trim().toUpperCase()}</strong> — not other branches of the same chain.</>}
        </div>
      )}

      {formDenied && (
        <div className="note note--warn">
          <strong>{formDenied.code} {formDenied.name} denies its input VAT (s.17(2)).</strong>
          <div style={{ margin: '6px 0' }}>
            A rule allocating to it must carry a denied VAT code, or every slip it matches claims
            input tax the Act does not allow — automatically, and with nothing on the face of the
            entry to say so.
          </div>
          {formDenied.default_tax_code_id && (
            <button className="btn btn--quiet"
                    onClick={() => setForm({ ...form, taxCodeId: formDenied.default_tax_code_id })}>
              Use {formDenied.default_tax_code}
            </button>
          )}
        </div>
      )}

      <button className="btn" style={{ marginTop: 8 }} onClick={create}
              disabled={busy || !form.supplierNorm.trim() || !form.accountId || Boolean(formDenied)}>
        {busy ? 'Saving…' : 'Add rule'}
      </button>

      <h2 className="eyebrow" style={{ margin: '30px 0 10px' }}>Rules</h2>
      <div className="controls">
        <label className="field" style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
          <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} />
          <span>Show deactivated rules</span>
        </label>
      </div>

      {visible.length === 0 ? (
        <Empty><p>No rules yet.</p><p>Add one above once you have confirmed an allocation on the review queue.</p></Empty>
      ) : (
        <div className="scroll-x">
          <table className="ledger">
            <thead>
              <tr>
                <th>Supplier</th>
                <th>Amount bounds</th>
                <th>Account</th>
                <th>VAT</th>
                <th className="num" style={{ width: 60 }}>Hits</th>
                <th style={{ width: 110 }}>Last fired</th>
                <th style={{ width: 210 }} />
              </tr>
            </thead>
            <tbody>
              {rules.filter((r) => showInactive || r.active).map((r) => (
                <tr key={r.id} style={r.active ? undefined : { opacity: 0.55 }}>
                  <td>
                    <code>{r.supplierNorm}</code>
                    {!r.active && <span className="neg"> · deactivated</span>}
                    {r.keyword && <div style={{ fontSize: 11, color: 'var(--ink-soft)' }}>keyword: {r.keyword}</div>}
                  </td>
                  {editing?.id === r.id ? (
                    <>
                      <td>
                        <input className="input--figure" style={{ width: 80 }} placeholder="Min"
                               value={editing.minAmount} onChange={(e) => setEditing({ ...editing, minAmount: e.target.value })} />
                        {' – '}
                        <input className="input--figure" style={{ width: 80 }} placeholder="Max"
                               value={editing.maxAmount} onChange={(e) => setEditing({ ...editing, maxAmount: e.target.value })} />
                      </td>
                      <td>
                        <select value={editing.accountId} style={{ width: '100%' }}
                                onChange={(e) => setEditing({ ...editing, accountId: e.target.value })}>
                          {accounts.map((a) => <option key={a.id} value={a.id}>{a.code} — {a.name}</option>)}
                        </select>
                      </td>
                      <td>
                        <select value={editing.taxCodeId} style={{ width: '100%' }}
                                onChange={(e) => setEditing({ ...editing, taxCodeId: e.target.value })}>
                          <option value="">No VAT</option>
                          {taxCodes.map((t) => <option key={t.id} value={t.id}>{t.code} — {t.name}</option>)}
                        </select>
                        {editDenied && (
                          <div className="neg" style={{ fontSize: 11, marginTop: 4 }}>
                            {editDenied.code} denies its input VAT — this needs a denied code
                            {editDenied.default_tax_code ? ` such as ${editDenied.default_tax_code}` : ''}.
                          </div>
                        )}
                      </td>
                      <td className="num figure">{r.hits}</td>
                      <td className="figure" style={{ fontSize: 12 }}>{r.lastHitAt ? r.lastHitAt.slice(0, 10) : 'never'}</td>
                      <td className="num">
                        <button className="icon-btn" onClick={saveEdit}
                                disabled={busy || Boolean(editDenied)}>Save</button>{' '}
                        <button className="icon-btn" onClick={() => setEditing(null)}>Cancel</button>
                      </td>
                    </>
                  ) : (
                    <>
                      <td className="figure" style={{ fontSize: 12 }}>
                        {r.minCents != null || r.maxCents != null
                          ? `${r.minCents != null ? money(centsToRand(r.minCents)) : 'any'} – ${r.maxCents != null ? money(centsToRand(r.maxCents)) : 'any'}`
                          : 'Any amount'}
                      </td>
                      <td>{accountLabel(r.accountId)}</td>
                      <td>{taxLabel(r.taxCodeId)}</td>
                      <td className="num figure">
                        {r.hits}
                        {r.hits === 0 && r.active && (
                          <div style={{ fontSize: 11 }} className="neg">never fired</div>
                        )}
                      </td>
                      <td className="figure" style={{ fontSize: 12 }}>{r.lastHitAt ? r.lastHitAt.slice(0, 10) : 'never'}</td>
                      <td className="num">
                        <button className="icon-btn" onClick={() => setEditing({
                          id: r.id, accountId: r.accountId, taxCodeId: r.taxCodeId ?? '',
                          minAmount: centsToRand(r.minCents), maxAmount: centsToRand(r.maxCents),
                          keyword: r.keyword ?? '',
                        })}>Edit</button>{' '}
                        <button className="icon-btn" onClick={() => setActive(r, !r.active)}>
                          {r.active ? 'Deactivate' : 'Reactivate'}
                        </button>
                      </td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="note">
        A rule never fired is either wrong, or written for a supplier the client has stopped using —
        worth checking rather than left to look authoritative.
      </div>
    </>
  );
}
